<template>
  <nav class="text-gray-500 mb-2 inline-flex">
    <ol class="list-none p-0 flex items-center text-sm">
      <li v-for="(crumb, index) in visibleCrumbs" :key="index" class="flex items-center">
        <template v-if="crumb.name === '...'">
          <button 
            class="mx-1 hover:text-gray-700 hover:underline cursor-pointer px-1 py-0.5 rounded transition-colors"
            title="Cliquer pour afficher tous les breadcrumbs"
            @click="toggleFullPath"
          >
            {{ crumb.name }}
          </button>
        </template>
        <template v-else>
          <div class="relative">
            <router-link 
              :to="crumb.path" 
              class="hover:underline relative"
              @click="handleCrumbClick(crumb, index)"
              @mouseenter="showTooltip(crumb, $event)"
              @mouseleave="hideTooltip"
            >
              {{ crumb.name }}
            </router-link>
            
            <!-- Tooltip personnalisé pour les noms longs -->
            <div 
              v-if="hoveredCrumb === crumb && isNameTruncated(crumb.name)"
              class="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg z-50 whitespace-nowrap max-w-xs"
              style="pointer-events: none;"
            >
              {{ getFullName(crumb) }}
            </div>
          </div>
        </template>
        <span v-if="index < visibleCrumbs.length - 1" class="mx-1">></span>
      </li>
    </ol>

    <!-- Modal discret pour les noms très longs -->
    <div 
      v-if="showModal && modalCrumb"
      class="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50"
      @click="closeModal"
    >
      <div 
        class="bg-white rounded-lg shadow-xl p-4 max-w-md mx-4"
        @click.stop
      >
        <h3 class="font-medium text-gray-900 mb-2">Nom complet</h3>
        <p class="text-sm text-gray-700 break-words">{{ modalCrumb.fullName }}</p>
        <button 
          class="mt-3 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
          @click="closeModal"
        >
          Fermer
        </button>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useBreadcrumbsStore } from '../stores/breadcrumbs';
import type { Crumb } from '../stores/breadcrumbs';

export default defineComponent({
  name: 'Breadcrumbs',
  setup() {
    const breadcrumbsStore = useBreadcrumbsStore();
    const hoveredCrumb = ref<Crumb | null>(null);
    const showModal = ref(false);
    const modalCrumb = ref<{ name: string; fullName: string } | null>(null);

    // Fonction pour gérer le clic sur une miette
    const handleCrumbClick = (crumb: Crumb, index: number): void => {
      // Si on clique sur une miette qui n'est pas la dernière,
      // on tronque l'historique jusqu'à cette miette
      if (index < breadcrumbsStore.visibleCrumbs.length - 1) {
        // Trouver l'index réel dans l'historique complet
        const realIndex = breadcrumbsStore.history.findIndex(item => item.path === crumb.path);
        if (realIndex !== -1) {
          // Tronquer l'historique jusqu'à cet index (inclus)
          breadcrumbsStore.history = breadcrumbsStore.history.slice(0, realIndex + 1);
        }
      }
    };

    // Fonction pour basculer l'affichage complet
    const toggleFullPath = (): void => {
      console.log('🔄 toggleFullPath appelé');
      //console.log('📊 Store breadcrumbs avant:', {
      //  history: breadcrumbsStore.history,
      //  showFullPath: breadcrumbsStore.showFullPath,
      //  visibleCrumbs: breadcrumbsStore.visibleCrumbs
      //});
      breadcrumbsStore.toggleFullPath();
      //console.log('📊 Store breadcrumbs après:', {
      //  history: breadcrumbsStore.history,
      //  showFullPath: breadcrumbsStore.showFullPath,
      //  visibleCrumbs: breadcrumbsStore.visibleCrumbs
      //});
    };

    // Fonction pour vérifier si un nom est tronqué
    const isNameTruncated = (name: string): boolean => {
      return name.endsWith('...');
    };

    // Fonction pour obtenir le nom complet
    const getFullName = (crumb: Crumb): string => {
      return crumb.fullName || crumb.name;
    };

    // Fonction pour afficher le tooltip
    const showTooltip = (crumb: Crumb, event: MouseEvent): void => {
      if (isNameTruncated(crumb.name)) {
        hoveredCrumb.value = crumb;
        
        // Si le nom est très long (plus de 50 caractères), afficher le modal au double-clic
        const fullName = getFullName(crumb);
        if (fullName.length > 50) {
          // Ajouter un écouteur pour le double-clic
          const target = event.target as HTMLElement;
          target.addEventListener('dblclick', () => {
            modalCrumb.value = { name: crumb.name, fullName };
            showModal.value = true;
          }, { once: true });
        }
      }
    };

    // Fonction pour masquer le tooltip
    const hideTooltip = (): void => {
      hoveredCrumb.value = null;
    };

    // Fonction pour fermer le modal
    const closeModal = (): void => {
      showModal.value = false;
      modalCrumb.value = null;
    };

    return {
      visibleCrumbs: breadcrumbsStore.visibleCrumbs,
      handleCrumbClick,
      toggleFullPath,
      hoveredCrumb,
      showModal,
      modalCrumb,
      isNameTruncated,
      getFullName,
      showTooltip,
      hideTooltip,
      closeModal
    };
  }
});
</script> 